/**
 * Representantes View - Vista de gestión de representantes
 * 
 * Funcionalidades:
 * - Visualización en tarjetas o lista
 * - Búsqueda y filtrado por estado
 * - Edición de representantes
 * - Visualización de detalles de representantes
 */

import React, { useEffect, useMemo, useState } from "react";
import Modal from "antd/es/modal/Modal";
import { Switch } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { listRepresentantes, updateRepresentante, deleteRepresentante, authorizeRepresentante } from "../../services/representantesService";
import { ModalRepresentante } from "../../modals/ModalRepresentante";
import useAuth from "../../hooks/useAuth";

/* ===== Constantes ===== */
const LS_REPS = "representantes_cache";

/* ===== Helpers ===== */
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function saveLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, saved_at: Date.now() }));
  } catch {}
}

function readLS(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw).value : null;
  } catch {
    return null;
  }
}

/**
 * Calcula la edad a partir de una fecha de nacimiento
 */
function calculateAge(birthDate) {
  if (!birthDate) return "—";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Normaliza los datos de un representante desde la API al formato usado en el componente
 */
function normalizeRepresentante(representante) {
  return {
    id: representante.id,
    auth_user_id: representante.auth_user_id,
    institucion_id: representante.institucion_id,
    rol_id: representante.rol_id,
    nombres: representante.nombres ?? "",
    apellidos: representante.apellidos ?? "",
    email: representante.email ?? "",
    fecha_nacimiento: representante.fecha_nacimiento,
    direccion: representante.direccion ?? "",
    cedula: representante.cedula ?? "",
    telefono: representante.telefono ?? "",
    observaciones: representante.observaciones ?? "",
    activo: representante.activo ?? true,
    authorized: representante.autorizado ?? false,
    // Datos calculados para visualización
    edad: calculateAge(representante.fecha_nacimiento),
    estado: representante.activo ? "Activo" : "Inactivo",
    institucion: representante.instituciones?.nombre ?? "—",
    registradoEn: representante.creado_en?.slice(0, 10) ?? "—",
  };
}

/* ===== UI Components ===== */
function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  
  const isStatusBadge = children === "Activo" || children === "Inactivo";
  const isActive = children === "Activo";
  
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1",
        variants[variant]
      )}
    >
      {isStatusBadge && (
        <span 
          className={clsx(
            "w-1.5 h-1.5 rounded-full",
            isActive ? "bg-emerald-500" : "bg-rose-500"
          )}
        />
      )}
      {children}
    </span>
  );
}

/* ===== Main View ===== */
export default function RepresentativesView() {
  const { user } = useAuth();
  const fallbackInstitution = "16020324-f0ea-4319-905c-5a30618ce9f5";
  const institutionId = user?.user_metadata?.institution_id || user?.user_metadata?.institucionId || fallbackInstitution;

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [onlyAuthorized, setOnlyAuthorized] = useState(true);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  
  // Modal estado
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modal delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [targetDelete, setTargetDelete] = useState(null);

  // Modal detail
  const [showDetail, setShowDetail] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [authorizing, setAuthorizing] = useState(false);

  const [error, setError] = useState("");

  // Función para cargar representantes (reutilizable)
  const loadRepresentantes = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await listRepresentantes(institutionId);
      const data = response?.data || response || [];
      const normalized = data.map(normalizeRepresentante);
      setList(normalized);
      saveLS(LS_REPS, normalized);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error cargando representantes");
      toast.error(e?.message || "Error cargando representantes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Carga optimista
    const cached = readLS(LS_REPS);
    if (cached) {
      setList(cached);
      setLoading(false);
    }

    if (!institutionId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const response = await listRepresentantes(institutionId);
        if (!mounted) return;
        
        const data = response?.data || response || [];
        const normalized = data.map(normalizeRepresentante);
        
        setList(normalized);
        saveLS(LS_REPS, normalized);
      } catch (e) {
        console.error("GET representantes failed:", e);
        if (mounted) {
          setError(e?.message || "Error cargando datos");
          toast.error("Error al cargar los datos iniciales");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    
    return () => {
      mounted = false;
    };
  }, [institutionId]);

  // Filtrado (case-insensitive)
  const filtered = useMemo(() => {
    const term = (query || "").toString().toLowerCase();
    return list.filter((r) => {
      const haystack = `${r.nombres || ""} ${r.apellidos || ""} ${r.email || ""} ${r.telefono || ""} ${r.cedula || ""}`;
      const matches = haystack.toLowerCase().includes(term);
      const active = onlyActive ? r.estado === "Activo" : true;
      const authorized = onlyAuthorized ? r.authorized === true : r.authorized === false;
      return matches && active && authorized;
    });
  }, [list, query, onlyActive, onlyAuthorized]);

  // Handlers
  function openEdit(row) {
    setEditing(row);
    setShowForm(true);
  }

  function askDelete(row) {
    setTargetDelete(row);
    setShowDelete(true);
  }

  function openDetail(row) {
    setSelectedDetail(row);
    setShowDetail(true);
  }
  
  function closeModal() {
    setShowForm(false);
    setShowDetail(false);
    setEditing(null);
    setSelectedDetail(null);
    setShowDelete(false);
    setTargetDelete(null);
    setDeleting(false);
  }

  async function onSubmit(formData) {
    if (!institutionId) return;

    setSaving(true);
    try {
      // Actualizar representante
      await updateRepresentante(editing.id, formData);
      
      // Recargar la lista completa desde el servidor
      await loadRepresentantes();
      
      toast.success("Representante actualizado correctamente");
      closeModal();
    } catch (e) {
      console.error("Save error:", e);
      setError(e?.message || "Error al guardar");
      toast.error(e?.message || "No se pudo actualizar el representante");
    } finally {
      setSaving(false);
    }
  }

  async function onConfirmDelete() {
    if (!targetDelete?.id) return;
    setDeleting(true);
    try {
      await deleteRepresentante(targetDelete.id);
      await loadRepresentantes();
      toast.success("Representante eliminado correctamente");
      closeModal();
    } catch (e) {
      console.error("Delete error:", e);
      toast.error(e?.message || "No se pudo eliminar el representante");
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleAuthorize(checked) {
    if (!selectedDetail) return;
    const idToUse = selectedDetail.auth_user_id || selectedDetail.id;
    if (!idToUse) {
      toast.error("No se encontró el id de usuario para autorizar");
      return;
    }

    setAuthorizing(true);
    try {
      await authorizeRepresentante(idToUse, checked);

      // Actualizar la vista detallada
      const updatedDetail = { ...selectedDetail, authorized: checked };
      setSelectedDetail(updatedDetail);

      // Actualizar la lista en memoria y cache
      setList((prev) => {
        const next = prev.map((r) => (r.id === updatedDetail.id ? { ...r, authorized: checked } : r));
        saveLS(LS_REPS, next);
        return next;
      });

      if (checked) {
        toast.success("Usuario autorizado correctamente");
      } else {
        toast.success("Autorización de usuario revocada");
      }
    } catch (e) {
      console.error("Authorize error:", e);
      toast.error(e?.message || "No se pudo cambiar la autorización");
    } finally {
      setAuthorizing(false);
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">Representantes</h1>
          <p className="text-slate-600 text-sm">
            Gestión de representantes de la institución.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex-1">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
            placeholder="Buscar por nombre, email, teléfono o cédula..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        {/* Checkbox mostrar solo activos */}
        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center">
              {onlyActive && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">Mostrar solo activos</span>
        </label>
        
        {/* Checkbox mostrar autorizados/no autorizados */}
        <label className="inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 select-none cursor-pointer hover:bg-slate-50 transition-colors">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={onlyAuthorized}
              onChange={(e) => setOnlyAuthorized(e.target.checked)}
              className="peer sr-only"
            />
            <div className="w-5 h-5 rounded border-2 border-slate-300 bg-white peer-checked:bg-emerald-600 peer-checked:border-emerald-600 transition-all flex items-center justify-center">
              {onlyAuthorized && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="font-medium">{onlyAuthorized ? "Solo autorizados" : "Solo no autorizados"}</span>
        </label>
        
        {/* Switch de vista */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de lista"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13" />
            </svg>
          </button>
        </div>
        
        <button
          onClick={loadRepresentantes}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-80 rounded-2xl border border-slate-200 bg-white overflow-hidden"
            >
              <div className="animate-pulse h-full bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {/* Vista de tarjetas */}
      {!loading && viewMode === "cards" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No se encontraron representantes</h3>
              <p className="text-sm text-slate-500">
                {query ? "Intenta ajustar los filtros de búsqueda" : "No hay representantes registrados"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((representante) => (
                <article
                  key={representante.id}
                  className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md transition"
                >
                  <div className="p-4">
                    {/* Header con avatar, info y botón de editar */}
                    <div className="flex items-start justify-between gap-1 mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-blue-100 text-blue-600 font-semibold text-2xl border border-slate-200 flex-shrink-0">
                          {representante.nombres?.charAt(0)}{representante.apellidos?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-base font-semibold truncate">{representante.nombres}</div>
                          <div className="text-sm text-slate-700 truncate">{representante.apellidos}</div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <Badge
                              variant={
                                representante.estado === "Activo" ? "success" : "danger"
                              }
                            >
                              {representante.estado}
                            </Badge>

                            {/* 🔖 Etiqueta de autorización */}
                            <Badge
                              variant={representante.authorized ? "info" : "muted"}
                            >
                              {representante.authorized ? "Autorizado" : "No autorizado"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Botón de editar en esquina */}
                      {representante.activo && (
                        <button
                          onClick={() => openEdit(representante)}
                          className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                          title="Editar representante"
                          aria-label="Editar"
                        >
                          <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {representante.activo && (
                        <button
                          onClick={() => askDelete(representante)}
                          className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                          title="Eliminar representante"
                          aria-label="Eliminar"
                        >
                          <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Información del representante */}
                    <div className="text-sm text-slate-700 grid grid-cols-2 gap-y-2 gap-x-3">
                      <span className="text-slate-500">Edad:</span>
                      <span>{representante.edad} años</span>
                      <span className="text-slate-500">Email:</span>
                      <span className="truncate">{representante.email}</span>
                      <span className="text-slate-500">Cédula:</span>
                      <span className="truncate">{representante.cedula || "—"}</span>
                      <span className="text-slate-500">Teléfono:</span>
                      <span>{representante.telefono || "—"}</span>
                      <span className="text-slate-500">Dirección:</span>
                      <span className="truncate">{representante.direccion || "—"}</span>
                      <span className="text-slate-500">Registrado:</span>
                      <span>{representante.registradoEn}</span>
                    </div>

                    {/* Botón Ver detalles */}
                    <button
                      onClick={() => openDetail(representante)}
                      className="w-full inline-flex items-center justify-center rounded-xl border border-slate-600 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50 hover:border-slate-500 active:scale-[.99] transition mt-4"
                    >
                      Ver detalles
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {/* Vista de lista */}
      {!loading && viewMode === "list" && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Representante
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Edad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Cédula
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Institución
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((representante) => (
                  <tr
                    key={representante.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600 font-semibold text-sm border border-slate-200">
                          {representante.nombres?.charAt(0)}{representante.apellidos?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {representante.nombres} {representante.apellidos}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {representante.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {representante.edad} años
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {representante.cedula || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                      {representante.telefono || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant="info">{representante.institucion}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge
                        variant={
                          representante.estado === "Activo" ? "success" : "danger"
                        }
                      >
                        {representante.estado}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openDetail(representante)}
                          className="inline-flex items-center justify-center rounded-lg border border-slate-300 text-slate-700 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 transition"
                          title="Ver detalles"
                        >
                          Ver detalles
                        </button>
                        <button
                          onClick={() => openEdit(representante)}
                          className="inline-flex items-center justify-center rounded-lg border border-blue-600 text-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 transition"
                          title="Editar representante"
                        >
                          Editar
                        </button>
                        {representante.activo && (
                          <button
                            onClick={() => askDelete(representante)}
                            className="inline-flex items-center justify-center rounded-lg border border-rose-600 text-rose-600 px-3 py-1.5 text-xs font-medium hover:bg-rose-50 hover:border-rose-400 transition"
                            title="Eliminar representante"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Empty state para lista */}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">No se encontraron representantes</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de edición */}
      <ModalRepresentante
        open={showForm}
        representante={editing}
        saving={saving}
        onCancel={closeModal}
        onSave={onSubmit}
      />

      {/* Modal Eliminar representante */}
      <Modal
        open={showDelete}
        onCancel={closeModal}
        title="Eliminar representante"
        footer={null}
        centered
      >
        <p className="text-sm text-slate-600 mb-4">
          ¿Seguro que deseas eliminar a{" "}
          <span className="font-medium text-slate-900">
            {targetDelete?.nombres} {targetDelete?.apellidos}
          </span>
          ? Esta acción no se puede deshacer.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={closeModal}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={deleting}
            className="rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>

      {/* Modal de detalles */}
      <Modal
        open={showDetail}
        onCancel={closeModal}
        title="Detalles del representante"
        footer={null}
        centered
        width={600}
      >
        {selectedDetail && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
              <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-blue-100 text-blue-600 font-semibold text-2xl border border-slate-200">
                {selectedDetail.nombres?.charAt(0)}{selectedDetail.apellidos?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  {selectedDetail.nombres} {selectedDetail.apellidos}
                </h3>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <Badge
                    variant={
                      selectedDetail.estado === "Activo" ? "success" : "danger"
                    }
                  >
                    {selectedDetail.estado}
                  </Badge>
                  <Badge
                    variant={selectedDetail.authorized ? "info" : "muted"}
                  >
                    {selectedDetail.authorized ? "Autorizado" : "No autorizado"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-600 mr-2 text-sm">Autorizar usuario</span>
                <Switch
                  checked={!!selectedDetail.authorized}
                  onChange={handleToggleAuthorize}
                  disabled={authorizing}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="block text-slate-500 mb-1">Email</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.email}</span>
              </div>
              <div>
                <span className="block text-slate-500 mb-1">Teléfono</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.telefono || "—"}</span>
              </div>
              <div>
                <span className="block text-slate-500 mb-1">Cédula</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.cedula || "—"}</span>
              </div>
              <div>
                <span className="block text-slate-500 mb-1">Edad</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.edad} años</span>
              </div>
              <div>
                <span className="block text-slate-500 mb-1">Fecha de nacimiento</span>
                <span className="block text-slate-900 font-medium">
                  {selectedDetail.fecha_nacimiento?.slice(0, 10) || "—"}
                </span>
              </div>
              <div>
                <span className="block text-slate-500 mb-1">Registrado</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.registradoEn}</span>
              </div>
              <div className="col-span-2">
                <span className="block text-slate-500 mb-1">Dirección</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.direccion || "—"}</span>
              </div>
              <div className="col-span-2">
                <span className="block text-slate-500 mb-1">Institución</span>
                <span className="block text-slate-900 font-medium">{selectedDetail.institucion}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
